<template>
  <div
    :class="{ 'aux-selector--templates': isCommentTemplates }"
    :style="containerStyle"
  >
    <!-- search area -->
    <selector-search-header
      ref="search_header"
      v-if="showSearchHeader"
      v-model="filterString"
      :new-value-label="newValueLabel"
      :show-close-button="!hideCloseButton"
      :hide-search-icon="isCommentTemplates"
      :use-own-clear-button="isCommentTemplates"
      :free-input-tooltip="isCommentTemplates ? $t('create_template') : ''"
      :show-free-input="
        allowFreeInput &&
        filterString.length &&
        searchCache.findIndex(
          (el) => el.Name.toLowerCase() === filterString.toLowerCase(),
        ) < 0
      "
      @keyDown="trackArrowDown(1)"
      @keyUp="trackArrowUp(1)"
      @keyEnter="
        () => {
          if (allowFreeInput && filterString.length > 0) {
            emitFreeInput(filterString);
          } else {
            selectCurrent();
          }
        }
      "
      @inputPressed="
        () => {
          if (allowFreeInput && filterString.length > 0) {
            emitFreeInput(filterString);
            if (!isCommentTemplates) closeItemMenu();
          }
        }
      "
      @close="closeItemMenu"
    />

    <div
      v-if="showSearchHeader && limitHeightToContainer"
      class="selector-subheader"
    >
      <span class="selector-subheader__label">{{
        isTag ? tagSegmentLabel : $t(strategy.label)
      }}</span>
      <span class="selector-subheader__reset" @click="resetSelection">{{
        $t("selector_reset")
      }}</span>
    </div>

    <!-- selector area -->
    <div class="row justify-center" :style="innerStyle" ref="innerSpace">
      <tag-selector
        v-if="isTag"
        :filter_string="filterString"
        :m-height="containerHeightPx > 0 ? containerHeightPx : height_variable"
        :full="full"
        :data-to-entity="resolvedFromFilter"
        :data-from-entity="resolvedToFilter"
        v-model="theModel"
      />
      <recursive-selector-refactored
        v-if="isParameter"
        ref="simple_selector"
        :take-ids-only="false"
        :allow-multiple="true"
        :element-type="9"
        v-model="theModel"
        :open-on-start="openOnStart"
        :filter-string="filterString"
        :viewport="$refs.innerSpace"
        return-type="none"
        nested-key-name="FieldFilters"
        entity-inside-name="Parameter"
      />
      <custom-hints-grid
        v-else-if="isCustomHints"
        :hints-data="customHintsData"
        :titles="customHintsTitles"
        :loading="customHintsLoading"
        :m-height="effectiveListHeightPx"
        @hint-selected="onCustomHintSelected"
      />
      <simple-selector
        ref="simple_selector"
        v-else
        :data-to-entity="resolvedFromFilter"
        :data-from-entity="resolvedToFilter"
        :object-filter="strategy.isObjectFilter"
        :load-more="boundLoadMore"
        :filter_value="strategy.isObjectFilter ? filterModel : filterString"
        :m-height="effectiveListHeightPx"
        :element-type="strategy.elementType"
        :storage-for-filter="storageForFilterValue"
        :hide-checkboxes="effectiveHideCheckboxes"
        :show-select-all="!!strategy.singleLoad"
        :single-load="!!strategy.singleLoad"
        :empty-hint="emptyHintKey"
        :no-matches-hint="noMatchesHintKey"
        :full="fullForSimpleSelector"
        @itemSelected="onItemSelected"
        @emptyClick="
          (e) => {
            if (allowFreeInput) {
              emitFreeInput('');
            }
            closeItemMenu();
          }
        "
        v-model="theModel"
        :allow-multiple="effectiveAllowMultiple"
        :has-deps="strategy.hasDeps"
        :is-storages-mode="isStoragesMode"
      >
        <template #item="{ item }">
          <!-- Шаблоны комментария: имя с подсказкой полного текста и корзина справа -->
          <div v-if="isCommentTemplates" class="aux-del-row">
            <span class="aux-tmpl-name">
              <simple-element
                :element="item"
                :elementType="strategy.elementType"
                :filter_string="filterString"
              />
              <q-tooltip
                v-if="item.Text"
                :delay="300"
                anchor="top left"
                self="bottom left"
                :offset="[0, 0]"
                class="aux-sel-tooltip aux-sel-tooltip--text"
              >
                {{ item.Text }}
              </q-tooltip>
            </span>
            <button
              v-if="canDeleteListItems"
              type="button"
              class="aux-del"
              :aria-label="$t('delete_template')"
              @mousedown.stop.prevent
              @click.stop="openDeleteConfirm($event, item)"
            >
              <span class="aux-del__ico"></span>
              <q-tooltip
                :delay="300"
                anchor="top middle"
                self="center middle"
                class="aux-sel-tooltip"
              >
                {{ $t("delete_template") }}
              </q-tooltip>
            </button>
          </div>
          <!-- Режим с удалением: строка списка делится на содержимое и корзину справа -->
          <div v-else-if="canDeleteListItems" class="aux-del-row">
            <simple-element
              :element="item"
              :elementType="strategy.elementType"
              :filter_string="filterString"
            />
            <button
              type="button"
              class="aux-del"
              :aria-label="$t('remove')"
              @mousedown.stop.prevent
              @click.stop="openDeleteConfirm($event, item)"
            >
              <span class="aux-del__ico"></span>
            </button>
          </div>
          <slot
            v-else
            name="item"
            :item="item"
            :element-type="strategy.elementType"
          >
            <parameter-inline-display
              v-if="strategy.elementType === VariableType.ParameterInline"
              id="ds"
              :editable="false"
              :is-table="true"
              :model-value="getPositionNameModel(item)"
            />
            <bill-row
              v-else-if="strategy.elementType === VariableType.Bill"
              :el="item.bill"
              :bill-type="item.billRowType"
            />
            <counterparty-row
              v-else-if="strategy.elementType === VariableType.Counterparty"
              :el="item"
            />
            <simple-element
              v-else
              :element="item"
              :elementType="strategy.elementType"
              :filter_string="filterString"
            />
          </slot>
        </template>
      </simple-selector>
    </div>

    <confirm-action-popup
      v-if="canDeleteListItems && deleteConfirmOpen"
      :title="deleteConfirmTitle"
      question=""
      :question-html="deleteConfirmQuestionHtml"
      :position="deleteMenuPos"
      @confirm="confirmDelete"
      @cancel="deleteConfirmOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from "vue-facing-decorator";
import TagSelector from "./TagSelector.vue";
import SimpleSelector from "./SimpleSelector.vue";
import RecursiveSelectorRefactored from "@/components/common/basic/recursive-selector/refactored/RecursiveSelector.vue";
import SelectorSearchHeader from "../../SelectorSearchHeader.vue";
import ParameterInlineDisplay from "@/components/multiedit/temporary/ParameterInlineDisplay.vue";
import BillRow from "@/components/multiedit/temporary/storageshared/BillRow.vue";
import CounterpartyRow from "@/components/multiedit/temporary/storageshared/CounterpartyRow.vue";
import SimpleElement from "@/components/common/basic/data-components/SimpleElement.vue";
import CustomHintsGrid from "../../CustomHintsGrid.vue";
import ConfirmActionPopup from "@/components/common/basic/confirm/ConfirmActionPopup.vue";

import { AuxSelectorDataType } from "../../types";
import {
  AuxSelectorFilter,
  VariableType,
  AnalyticsV2ElementType,
} from "@/models/etc/types";
import { rootStore } from "@/store/index";
import {
  strategies,
  resolveFromFilter,
  resolveToFilter,
} from "../strategies/index";
import type { SelectorStrategy, StrategyContext } from "../strategies/types";
import {
  ParameterInstanceContainerType,
  V2FieldValueDisplayType,
  V2RecordAxisType,
} from "@/models/etc/types";
import { V2ParameterValueType } from "@/models/etc/parameter-types";
import type {
  ParameterInstance,
  ParameterInstanceContainer,
  ParameterInstanceContainerFilled,
} from "@/models/interfaces/parameters/parameter-models";
import type {
  StorageItemSearched,
  CustomParameterHintsResponse,
  ParameterContainerFilledHintsRequest,
  ParameterHintView,
  ParameterHintNoteView,
  HintView,
} from "@/models/interfaces/storage/storage-models";
import { storagePositionsStore } from "@/store/storage/storage-positions";
import { reactive } from "vue";
import { i18n } from "@/i18n";

const TAG_SEGMENT_MAP: Record<number, string> = {
  [AnalyticsV2ElementType.Client]: "tags_of_clients",
  [AnalyticsV2ElementType.Project]: "tags_of_projects",
  [AnalyticsV2ElementType.Person]: "tags_of_contacts",
  [AnalyticsV2ElementType.Job]: "tags_of_jobs",
  [AnalyticsV2ElementType.File]: "tags_of_files",
};

@Component({
  name: "AuxSelector",
  components: {
    TagSelector,
    SimpleSelector,
    RecursiveSelectorRefactored,
    SelectorSearchHeader,
    ParameterInlineDisplay,
    BillRow,
    CounterpartyRow,
    SimpleElement,
    CustomHintsGrid,
    ConfirmActionPopup,
  },
  expose: ["trackArrowUp", "trackArrowDown", "selectCurrent"],
})
export default class AuxSelector extends Vue {
  VariableType = VariableType;

  // --- Props ---
  @Prop({ default: [] })
  modelValue!: any[];
  @Emit("update:modelValue") emitModelValue(arg: any[]) {
    return arg;
  }

  @Prop({ default: null })
  filterModelValue!: string;
  @Emit("update:filterModelValue") emitFilterModelValue(arg: string) {
    return arg;
  }

  @Prop({ default: AuxSelectorDataType.Tags })
  modelType!: AuxSelectorDataType;

  @Emit("selectorClosed") emitSelectorClosed(arg: any) {
    return arg;
  }
  @Emit("deleteItem") emitDeletedItem(arg: any) {
    return arg;
  }
  @Emit("inputValue") emitFreeInput(arg: string) {
    return arg;
  }
  @Emit("chunkLoaded") emitLoaded(arg: any) {
    return arg;
  }
  @Emit("selectedHint") emitSelectedHint(arg: any) {
    return arg;
  }
  @Emit("hintedSetType") emitHintedSetType(arg: any) {
    return arg;
  }
  @Emit("dataAvailable") emitDataAvailable(arg: boolean) {
    return arg;
  }

  filter_string: string = "";

  @Prop({ default: -1 }) inputIndex!: number;
  @Prop({ default: 0 }) setId?: number;
  @Prop({ default: 0 }) instanceId?: number;
  @Prop({ default: true }) isParameterInstance?: boolean;
  @Prop({ default: false }) allowFreeInput?: boolean;
  @Prop({ default: false }) allowMultiple?: boolean;
  @Prop({ default: true }) showSearchHeader?: boolean;
  @Prop({ default: false }) hideCloseButton?: boolean;
  @Prop({ default: false }) limitHeightToContainer?: boolean;
  @Prop({ default: 0 }) containerHeightPx!: number;
  @Prop({ default: null }) componentModel!: {
    vendorCode: string;
    container: ParameterInstanceContainerFilled;
    storageForFilter?: number;
    possibleValueSetsIds?: number[];
  };
  @Prop({ default: -1 }) excludeId!: number;
  @Prop({ default: -1 }) preferredSegment!: number;
  @Prop({ default: false }) openOnStart!: boolean;
  @Prop({ default: false }) hideCheckboxes!: boolean;
  @Prop({ default: false }) full!: boolean;
  @Prop({ default: true }) useRefactoredRecursive!: boolean;

  height_variable: number = 0;
  private resizeObs: ResizeObserver | null = null;
  searchCache: any[] = reactive([]);
  customHintsData: ParameterHintView[] = reactive([]);
  customHintsTitles: { Id: number; Name: string }[] = reactive([]);
  customHintsLoading = false;

  deleteConfirmOpen = false;
  deleteMenuPos: { x: number; y: number } = { x: 0, y: 0 };
  itemToDelete: any = undefined;

  // --- Strategy ---

  get strategy(): SelectorStrategy {
    return strategies[this.modelType];
  }

  get resolvedFromFilter() {
    return resolveFromFilter(this.strategy);
  }

  get resolvedToFilter() {
    const toFilter = resolveToFilter(this.strategy);
    const fromFilter = resolveFromFilter(this.strategy);
    return (ent: any) => {
      const orig = this.theModel?.find((m: any) => {
        try {
          return fromFilter(m)?.Id === ent?.Id;
        } catch {
          return false;
        }
      });
      return toFilter(ent, orig);
    };
  }

  get strategyContext(): StrategyContext {
    return {
      setId: this.setId ?? 0,
      excludeId: this.excludeId,
      preferredSegment: this.preferredSegment,
      componentModel: this.componentModel,
      allowFreeInput: this.allowFreeInput ?? false,
      searchCache: this.searchCache,
      t: (key: string) => i18n.global.t(key),
      emit: (event: string, payload?: any) => this.$emit(event, payload),
      instanceId: this.instanceId ?? 0,
      isParameterInstance: this.isParameterInstance ?? true,
    };
  }

  get boundLoadMore() {
    return (
      filter: string | AuxSelectorFilter,
      count: number,
      offset: number,
    ) => this.strategy.loadMore(filter, count, offset, this.strategyContext);
  }

  // --- Computed ---

  get tagSegmentLabel(): string {
    const mode = rootStore().generalMode;
    return this.$t(TAG_SEGMENT_MAP[mode] ?? "selector_system_tags");
  }

  get isTag(): boolean {
    return this.modelType === AuxSelectorDataType.Tags;
  }

  get isParameter(): boolean {
    return this.modelType === AuxSelectorDataType.Parameters;
  }

  get isStoragesMode(): boolean {
    // В фильтрах складов используются оба режима: Storages (22) и MovementStorages (23).
    return (
      this.modelType === AuxSelectorDataType.Storages ||
      this.modelType === AuxSelectorDataType.MovementStorages
    );
  }

  get isCustomHints(): boolean {
    return this.modelType === AuxSelectorDataType.CustomParameterHints;
  }

  get effectiveHideCheckboxes(): boolean {
    return this.hideCheckboxes || this.strategy.hideCheckboxes;
  }

  // Корзину в строке показываем только если стратегия умеет удалять и права позволяют.
  // Права проверяем тут, а не при создании стратегии: сторы к тому моменту ещё не готовы.
  get canDeleteListItems(): boolean {
    if (!this.strategy.deleteItem) return false;
    return this.strategy.canDeleteItems ? this.strategy.canDeleteItems() : true;
  }

  get deleteConfirmTitle(): string {
    return String(this.$t("confirm_delete_template_title"));
  }

  // Имя подставляем в вопрос жирным, поэтому экранируем его перед вставкой в v-html.
  get deleteConfirmQuestionHtml(): string {
    const name = String(this.itemToDelete?.Name ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
    return String(
      (this.$t as any)("confirm_delete_template_question", {
        name: `<b>«${name}»</b>`,
      }),
    );
  }

  get effectiveAllowMultiple(): boolean {
    if (this.strategy.allowMultipleOverride !== undefined)
      return this.strategy.allowMultipleOverride;
    // ВАЖНО: возвращаем строго prop, без `|| true`. Раньше `this.allowMultiple || true`
    // делало эффективное значение всегда true и маскировало явный `:allow-multiple="false"`.
    // Все потребители <aux-selector>, рассчитывающие на multi-select, должны передавать
    // `:allow-multiple="true"` явно (default остаётся false как у самого пропа).
    return !!this.allowMultiple;
  }

  get fullForSimpleSelector(): boolean {
    return this.full || this.strategy.hasDeps;
  }

  get storageForFilterValue(): number {
    if (
      this.modelType === AuxSelectorDataType.StorageItems &&
      (this.componentModel?.storageForFilter ?? 0)
    ) {
      return this.componentModel?.storageForFilter ?? 0;
    }
    return 0;
  }

  // Шаблоны комментария: аналитика, позиция склада, действие склада. Для них окно
  // фиксированного размера и своё оформление строк.
  get isCommentTemplates(): boolean {
    return (
      this.modelType === AuxSelectorDataType.TableCommentTemplates ||
      this.modelType === AuxSelectorDataType.StorageItemCommentTemplates ||
      this.modelType === AuxSelectorDataType.MovementCommentTemplates
    );
  }

  // Подсказка вместо пустого списка, когда фильтр не введён. Только там, где элемент
  // можно создать прямо из селектора (кнопка с плюсом при allowFreeInput).
  get emptyHintKey(): string {
    if (!this.allowFreeInput) return "";
    if (this.isCommentTemplates) return "create_first_template";
    if (this.modelType === AuxSelectorDataType.StringSets) return "create_first_value";
    if (this.modelType === AuxSelectorDataType.Lists) return "create_first_list";
    return "";
  }

  get noMatchesHintKey(): string {
    if (!this.allowFreeInput) return "";
    if (this.isCommentTemplates) return "change_query_or_create_template";
    if (this.modelType === AuxSelectorDataType.StringSets) return "change_query_or_create_value";
    if (this.modelType === AuxSelectorDataType.Lists) return "change_query_or_create_list";
    return "";
  }

  get containerStyle(): string {
    if (this.isCommentTemplates) {
      return "width: 320px; height: 260px; display: flex; flex-direction: column; overflow: hidden;";
    }
    const isFullTag = this.full && this.isTag;
    const isFullDeps = this.fullForSimpleSelector && this.strategy.hasDeps;
    // Внутри SelectorPopup (limitHeightToContainer): width/max-width 100% + overflow —
    // иначе у grid с колонками 1fr неопределённая ширина контейнера (тянется по max-content),
    // колонки не сужаются и текст не переносится.
    let sizeStyle: string;
    if (this.limitHeightToContainer) {
      sizeStyle = "width: 100%; max-width: 100%; overflow: hidden;";
    } else if (this.modelType === AuxSelectorDataType.Lists) {
      sizeStyle = "min-width: 240px;";
    } else if (isFullTag || isFullDeps) {
      sizeStyle = "min-width: 50vw;";
    } else {
      sizeStyle = "";
    }
    return sizeStyle + (this.limitHeightToContainer ? " height: 100%;" : "");
  }

  get newValueLabel(): string {
    return this.modelType === AuxSelectorDataType.Lists
      ? this.$t("create_list") + ' "' + this.filterString + '"'
      : this.$t("save");
  }

  get effectiveListHeightPx(): number {
    if (this.containerHeightPx > 0) return this.containerHeightPx;
    return this.height_variable > 0 &&
      (!this.strategy.isObjectFilter || this.limitHeightToContainer)
      ? this.height_variable - 4
      : 0;
  }

  get innerStyle(): string {
    // Окно шаблонов фиксировано по высоте: 32 на поле поиска плюс его отступы 12 и 8.
    if (this.isCommentTemplates) {
      return "height: calc(100% - 52px); overflow: hidden;";
    }
    const subheaderHeight =
      this.showSearchHeader && this.limitHeightToContainer ? 26 : 0;
    return `height: ${
      this.limitHeightToContainer
        ? this.showSearchHeader
          ? `calc(100% - ${52 + subheaderHeight}px)`
          : "100%"
        : `calc(${
            this.allowFreeInput &&
            this.modelType !== AuxSelectorDataType.Parameters
              ? 45
              : 60
          }vh - 80px)`
    };
      overflow: ${
        this.modelType === AuxSelectorDataType.Parameters ? "auto" : "hidden"
      }; ${this.allowFreeInput ? "max-width: 700px;" : ""}${
      this.limitHeightToContainer
        ? ` padding-left: ${this.strategy.hasDeps ? 12 : 8}px;`
        : ""
    }`;
  }

  resetSelection() {
    // Temporary integration for refactored recursive selector in filters:
    // reset should clear only node selection flags, not remove the full tree model.
    if (this.isParameter) {
      this.clearRecursiveSelection(this.theModel);
      this.theModel = [...this.theModel];
      return;
    }
    this.theModel = [];
  }

  private clearRecursiveSelection(nodes: any[]) {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((node: any) => {
      if (!node) return;
      if (Object.prototype.hasOwnProperty.call(node, "Selected"))
        node.Selected = false;
      if (Object.prototype.hasOwnProperty.call(node, "Positive"))
        node.Positive = false;
      const children = node?.FieldFilters;
      if (Array.isArray(children) && children.length)
        this.clearRecursiveSelection(children);
    });
  }

  // --- Model passthrough (MUST stay identical to original) ---

  get theModel(): any[] {
    return this.modelValue;
  }

  set theModel(val: any[]) {
    this.emitModelValue(val);
  }

  get hasExternalFilterModel(): boolean {
    return (
      this.filterModelValue !== null && this.filterModelValue !== undefined
    );
  }

  get filterString(): string {
    return this.hasExternalFilterModel
      ? this.filterModelValue
      : this.filter_string;
  }

  set filterString(val: string) {
    if (this.hasExternalFilterModel) {
      this.emitFilterModelValue(val);
      return;
    }
    this.filter_string = val;
  }

  get filterModel(): AuxSelectorFilter {
    return reactive({
      stringFilter: this.filterString,
      objFilter: {
        setId: this.setId,
        component: this.componentModel,
      },
    } as AuxSelectorFilter);
  }

  set filterModel(val: AuxSelectorFilter) {
    this.filterString = val.stringFilter;
  }

  // --- Child method proxies ---

  trackArrowUp(arg: number) {
    this.$refs.simple_selector &&
      (this.$refs.simple_selector as any).trackArrowUp(arg);
  }

  trackArrowDown(arg: number) {
    this.$refs.simple_selector &&
      (this.$refs.simple_selector as any).trackArrowDown(arg);
  }

  selectCurrent(): boolean {
    return this.$refs.simple_selector
      ? (this.$refs.simple_selector as any).selectCurrent()
      : false;
  }

  // --- Events ---

  onItemSelected() {
    if (this.strategy.closeAfterSelect && !this.effectiveAllowMultiple) {
      this.closeItemMenu();
    }
  }

  // --- Position name model (for StorageItems rendering) ---

  getPositionNameModel(
    item: StorageItemSearched,
  ): ParameterInstanceContainer[] {
    const insts = item.NameParameters[0].Instance;
    let res: any[] =
      item.NameParameters.length === 1 &&
      item.NameParameters[0].Type ===
        ParameterInstanceContainerType.SingleInstance &&
      insts
        ? insts.RecordFields
        : item.NameParameters;

    if (item.VendorCode) {
      const vcodeCell = {
        Type: ParameterInstanceContainerType.SingleInstance,
        Instance: {
          TemplateId: 999,
          Unremovable: true,
          ValueType: V2ParameterValueType.String,
          StringValue: "• " + item.VendorCode,
          NumberValue: null,
          DateValue: null,
          BoolValue: null,
          ValueSet: null,
          SelectedValue: null,
          ValueDisplayType: V2FieldValueDisplayType.Inline,
          Name: this.$t("sku"),
          Id: 0,
          Round: 2,
          RecordAxisType: V2RecordAxisType.Horizontal,
          RecordFields: [],
        } as ParameterInstance,
        ListTitle: null,
      } as ParameterInstanceContainer;
      res = [vcodeCell, ...res];
    }

    const storageCell = {
      Type: ParameterInstanceContainerType.SingleInstance,
      Instance: {
        TemplateId: 998,
        Unremovable: true,
        ValueType: V2ParameterValueType.String,
        StringValue: item.QuantitiesInStock.map(
          (q) => q.Name + " (" + q.Count + ")",
        ).join(", "),
        NumberValue: null,
        DateValue: null,
        BoolValue: null,
        ValueSet: null,
        SelectedValue: null,
        ValueDisplayType: V2FieldValueDisplayType.Inline,
        Name: this.$t("warehouse"),
        Id: 0,
        Round: 2,
        RecordAxisType: V2RecordAxisType.Horizontal,
        RecordFields: [],
      } as ParameterInstance,
      ListTitle: null,
    } as ParameterInstanceContainer;
    res.push(storageCell);

    return res;
  }

  // --- Created item signal ---

  /* Шаблон создаёт родитель по событию inputValue, поэтому селектор не знает, когда запрос
     дошёл. Сторы после успешного создания подменяют объект-маркер. По его смене обновляем
     выдачу, меню при этом остаётся открытым и список показывает новый шаблон. */
  get createdSignalValue(): any {
    return this.strategy.createdSignal?.();
  }

  @Watch("createdSignalValue")
  onCreatedSignalChange() {
    if (!this.isCommentTemplates) return;
    (this.$refs.simple_selector as any)?.reload?.();
  }

  // --- Custom hints ---

  @Watch("componentModel", { deep: true })
  onComponentModelChange() {
    if (this.isCustomHints) {
      this.loadCustomHints();
    }
  }

  async loadCustomHints() {
    const store = storagePositionsStore();
    if (!store || !this.componentModel?.container) return;

    this.customHintsLoading = true;
    try {
      const response =
        (await store.instanceGetHintsToInstanceContainerFilledCustom({
          Container: this.componentModel.container,
          HintedTemplateId: this.setId,
          Count: 100000,
          Offset: 0,
          Filter: "",
        } as ParameterContainerFilledHintsRequest)) as CustomParameterHintsResponse;

      if (!response?.Hints) {
        this.customHintsLoading = false;
        this.emitDataAvailable(false);
        return;
      }

      const possibleSets = this.componentModel.possibleValueSetsIds || [];

      const titleMap = new Map<number, string>();
      for (const hint of response.Hints) {
        for (const note of hint.Notes) {
          if (!titleMap.has(note.ValueSetId)) {
            titleMap.set(note.ValueSetId, note.ValueSetName);
          }
        }
      }
      const allValueSetIds = Array.from(titleMap.keys());
      this.customHintsTitles.splice(0);
      this.customHintsTitles.push(
        { Id: -1, Name: response.HintedSetName || "Значение" },
        ...allValueSetIds.map((id) => ({ Id: id, Name: titleMap.get(id)! })),
      );

      const mapped: ParameterHintView[] = response.Hints.map((hint) => {
        const noteMap = new Map<number, ParameterHintNoteView>();
        for (const n of hint.Notes) {
          noteMap.set(n.ValueSetId, {
            ValueSetId: n.ValueSetId,
            ValueSetName: n.ValueSetName,
            CanBeInsert: possibleSets.includes(n.ValueSetId),
            Visible: false,
            SelectedHint: null,
            HintsLenght: (n.HintsLenght ?? 30) * 14,
            Hints: n.Hints.map(
              (h) =>
                ({
                  Id: h.Id,
                  Name: h.Name,
                  NameBack: h.Name,
                  Count: h.UsageCount,
                  Selected: false,
                  Visible: true,
                } as HintView),
            ),
          });
        }
        const alignedNotes: (ParameterHintNoteView | null)[] =
          allValueSetIds.map((vsId) => noteMap.get(vsId) ?? null);
        return {
          HintedValueId: hint.HintedValueId,
          FilteringValue: hint.FilteringValue,
          HintedValue: hint.HintedValue,
          Notes: alignedNotes,
          UsageCount: hint.UsageCount,
        };
      });

      this.customHintsData.splice(0);
      this.customHintsData.push(...mapped);
    } catch (e) {
      console.error("Failed to load custom hints", e);
    }
    this.customHintsLoading = false;
    this.emitDataAvailable(this.customHintsData.length > 0);
  }

  onCustomHintSelected(payload: {
    value: { Id: number; Name: string };
    notes: [number, { Id: number; Name: string }][];
  }) {
    if (payload.notes.length) {
      this.emitSelectedHint(payload.notes);
    }
    this.emitModelValue([payload.value]);
    this.closeItemMenu();
  }

  // --- Lifecycle ---

  mounted() {
    this.searchCache.splice(0);
    this.focusSearchWithRetry(0);

    this.resizeObs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.height_variable = entry.contentRect.height;
      }
    });
    this.$nextTick(() => {
      if (this.$refs.innerSpace) {
        this.resizeObs!.observe(this.$refs.innerSpace as HTMLElement);
      }
    });

    if (this.isCustomHints) {
      this.loadCustomHints();
    }
  }

  beforeUnmount() {
    this.resizeObs?.disconnect();
    this.strategy.resetCache?.();
  }

  /**
   * Фокус в поиск после mount: вложенный q-dialog + телепорт попапа дают позднюю готовность DOM;
   * без нескольких попыток header ещё не смонтирован — поле не фокусируется.
   */
  focusSearchWithRetry(attempt: number) {
    const maxAttempts = 4;
    setTimeout(
      () => {
        const headerRef = this.$refs.search_header as any;
        if (headerRef?.focusField) {
          headerRef.focusField();
        }
        if (attempt < maxAttempts) {
          this.focusSearchWithRetry(attempt + 1);
        }
      },
      attempt === 0 ? 40 : 100,
    );
  }

  // Точку берём у корзины, в границы экрана окно вписывает сам ConfirmActionPopup.
  openDeleteConfirm(ev: MouseEvent, item: any) {
    const btn = ev.currentTarget as HTMLElement | null;
    const rect = btn?.getBoundingClientRect();
    this.deleteMenuPos = rect
      ? { x: Math.round(rect.left), y: Math.round(rect.bottom + 2) }
      : { x: ev.clientX, y: ev.clientY };
    this.itemToDelete = item;
    this.deleteConfirmOpen = true;
  }

  async confirmDelete() {
    const item = this.itemToDelete;
    this.deleteConfirmOpen = false;
    this.itemToDelete = undefined;
    if (!item || !this.strategy.deleteItem) return;

    const done = await this.strategy.deleteItem(item, this.strategyContext);
    if (!done) return;

    // Список правим на месте, чтобы не перезагружать страницу выдачи и не терять позицию скролла.
    (this.$refs.simple_selector as any)?.removeItem?.(item);
    const cached = this.searchCache.findIndex((el: any) => el?.Id === item?.Id);
    if (cached > -1) {
      this.searchCache.splice(cached, 1);
    }
    this.emitDeletedItem(item);
  }

  closeItemMenu() {
    this.searchCache.splice(0);
    this.strategy.resetCache?.();
    this.emitSelectorClosed(this.inputIndex);
  }
}
</script>

<style lang="scss">
@import "../styles/selector-shared";
</style>
